import { useEffect, useRef, useState } from 'react';
import { FlexStack } from '~/components/atomic';
import InputLabelTitle from '~/components/common/SignIn/Input/InputLabelTitle/InputLabelTitle';
import { isOver13YearsOld, isValidDate } from '~/utils';
import { useSignUpStepStore } from '~/store';
import ErrorText from '../Error/ErrorText';
import * as S from './Profile.styled';

interface BirthProps {
  setBirthValid: (state: boolean) => void;
}

export default function Birth({ setBirthValid }: BirthProps) {
  const { birth, setBirth } = useSignUpStepStore((state) => ({
    birth: state.profile.birth,
    setBirth: state.setBirth,
  }));

  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [error, setError] = useState('');

  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fullDate = month + day + year;
    setBirth(fullDate);

    if (year !== '' && Number(year) < 1900) {
      setError('Only individuals born after 1900 can sign up.');
      setBirthValid(false);
    }

    if (fullDate.length === 8 && Number(year) >= 1900) {
      if (!isValidDate(fullDate)) {
        setError('Please enter a valid date of birth.');
        setBirthValid(false);
      } else if (!isOver13YearsOld(fullDate)) {
        setError('You must be 13 years or older to sign up.');
        setBirthValid(false);
      } else {
        setError('');
        setBirthValid(true);
      }
    } else {
      setBirthValid(false);
    }
  }, [day, month, year, birth]);

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    if (/^\d*$/.test(value)) {
      if (value.length === 1 && value !== '0' && value !== '1') {
        value = '0' + value;
        setMonth(value);
        dayRef.current?.focus();
      } else {
        setMonth(value);
        if (value.length === 2) {
          dayRef.current?.focus();
        }
      }
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      setDay(value);
      if (value.length === 2) {
        yearRef.current?.focus();
      }
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (/^\d*$/.test(value)) {
      setYear(value);
    }
  };

  const handleDayBlur = () => {
    if (dayRef.current?.value.length === 1) {
      setDay('0' + day);
    }
  };

  return (
    <S.Container>
      <FlexStack spacing={4}>
        <InputLabelTitle title='Date of birth' />
        <S.BirthBox>
          <S.InputBox>
            <S.Input
              type='tel'
              id='SignUpProfileMonth'
              placeholder='Month'
              maxLength={2}
              value={month}
              onChange={handleMonthChange}
              ref={monthRef}
            />
          </S.InputBox>
          <S.InputBox>
            <S.Input
              type='tel'
              id='SignUpProfileDay'
              placeholder='Day'
              maxLength={2}
              value={day}
              onChange={handleDayChange}
              onBlur={handleDayBlur}
              ref={dayRef}
            />
          </S.InputBox>
          <S.InputBox>
            <S.Input
              type='tel'
              id='SignUpProfileYear'
              placeholder='Year'
              maxLength={4}
              value={year}
              onChange={handleYearChange}
              ref={yearRef}
            />
          </S.InputBox>
        </S.BirthBox>
      </FlexStack>
      <S.NotiText>Use only to verify your age</S.NotiText>
      <ErrorText error={error} />
    </S.Container>
  );
}
